/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83, Figma 08·04 "Хорошая связь"): the quality of our own
 * connection to the SFU, as LiveKit measures it.
 */

import { fromEvent, map, of, startWith, switchMap } from "rxjs";
import {
  ConnectionQuality,
  type LocalParticipant,
  ParticipantEvent,
} from "livekit-client";

import { type Behavior } from "../state/Behavior";
import { type ObservableScope } from "../state/ObservableScope";
import { type Connection } from "../state/CallViewModel/remoteMembers/Connection";

/** null while LiveKit does not know yet. */
export type IonConnectionQuality = "good" | "poor" | "lost" | null;

export function ionQuality(q: ConnectionQuality): IonConnectionQuality {
  switch (q) {
    case ConnectionQuality.Excellent:
    case ConnectionQuality.Good:
      return "good";
    case ConnectionQuality.Poor:
      return "poor";
    case ConnectionQuality.Lost:
      return "lost";
    default:
      return null;
  }
}

/** The quality of the local connection, null without one. */
export function createConnectionQuality$(
  scope: ObservableScope,
  localConnection$: Behavior<Connection | null>,
): Behavior<IonConnectionQuality> {
  return scope.behavior(
    localConnection$.pipe(
      switchMap((connection) => {
        if (!connection) return of(null);
        const participant: LocalParticipant =
          connection.livekitRoom.localParticipant;
        return fromEvent(
          participant,
          ParticipantEvent.ConnectionQualityChanged,
          (q: ConnectionQuality) => q,
        ).pipe(startWith(participant.connectionQuality), map(ionQuality));
      }),
    ),
    null,
  );
}
